import { INK, ORB, paletteColor, shadeOf } from '@blorbs/common';
import type { EyeStyle, MouthStyle, PlayerState, Vec } from '@blorbs/common';
import type { Camera } from './camera';
import type { Deform } from './squish';
import { circle, ellipse, inkStroke } from './art';

export type Face = 'idle' | 'hit' | 'dead' | 'cheer';

const HIT_FACE_MS = 450;
const HIT_FLASH_MS = 120;

export function faceFor(p: PlayerState, serverNow: number, isWinner: boolean): Face {
  if (!p.alive) return 'dead';
  if (isWinner) return 'cheer';
  if (serverNow - p.lastHitAt < HIT_FACE_MS) return 'hit';
  return 'idle';
}

export function drawOrb(ctx: CanvasRenderingContext2D, cam: Camera, p: PlayerState, face: Face, serverNow: number, isMe: boolean, deform?: Deform): void {
  const r = ORB.radius;
  const primary = paletteColor(p.skin.primary);
  const shade = shadeOf(primary);
  const dead = face === 'dead';

  // Ground shadow (undeformed: it's the ground, not the blob). It shrinks and
  // lightens as the body hovers up, and on each inhale while standing.
  const lift = deform?.lift ?? 0;
  const puff = deform?.puff ?? 0;
  const shadowK = Math.max(0.4, 1 - lift * 0.07 - puff * 0.4);
  ctx.fillStyle = `rgba(65,76,93,${0.1 + 0.12 * shadowK})`;
  ellipse(ctx, p.x, p.y + r * 0.95, r * 0.95 * shadowK, r * 0.3 * (0.6 + 0.4 * shadowK));
  ctx.fill();

  ctx.save();
  if (dead) ctx.globalAlpha = 0.5;
  // Hover, then squash & stretch. Standing, the vertical stretch is anchored at
  // the base so the blob rises off the ground instead of sinking into it.
  if (lift) ctx.translate(0, -lift);
  if (deform && (deform.sx !== 1 || deform.sy !== 1)) {
    if (deform.upright) ctx.translate(0, -r * (deform.sx - 1));
    ctx.translate(p.x, p.y);
    ctx.rotate(deform.angle);
    ctx.scale(deform.sx, deform.sy);
    ctx.rotate(-deform.angle);
    ctx.translate(-p.x, -p.y);
  }

  // Body: shade base, primary offset up-left, clipped to the disc.
  circle(ctx, p.x, p.y, r);
  ctx.fillStyle = shade;
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = primary;
  circle(ctx, p.x - r * 0.16, p.y - r * 0.2, r);
  ctx.fill();
  // Hit flash.
  if (face !== 'dead' && serverNow - p.lastHitAt < HIT_FLASH_MS) {
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.fillRect(p.x - r, p.y - r, r * 2, r * 2);
  }
  ctx.restore();

  // Highlight.
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ellipse(ctx, p.x - r * 0.4, p.y - r * 0.45, r * 0.2, r * 0.12, -0.6);
  ctx.fill();

  // Outline.
  inkStroke(ctx, isMe ? 3.4 : 3, cam.scale);
  circle(ctx, p.x, p.y, r);
  ctx.stroke();

  drawFace(ctx, cam, p, face);
  ctx.restore();

}

function drawFace(ctx: CanvasRenderingContext2D, cam: Camera, p: PlayerState, face: Face): void {
  const r = ORB.radius;
  const aim = p.aim;
  const eyeY = p.y - r * 0.1;
  const eyeDX = r * 0.34;
  const mouthY = p.y + r * 0.34;

  if (face === 'dead') {
    drawXEyes(ctx, cam, p.x - eyeDX, eyeY, r * 0.16);
    drawXEyes(ctx, cam, p.x + eyeDX, eyeY, r * 0.16);
    inkStroke(ctx, 2.2, cam.scale);
    ctx.beginPath();
    ctx.moveTo(p.x - r * 0.28, mouthY + r * 0.05);
    ctx.lineTo(p.x + r * 0.28, mouthY + r * 0.05);
    ctx.stroke();
    return;
  }

  if (face === 'hit') {
    drawWhiteEye(ctx, cam, p.x - eyeDX, eyeY, r * 0.26, aim, r * 0.1, r * 0.04);
    drawWhiteEye(ctx, cam, p.x + eyeDX, eyeY, r * 0.26, aim, r * 0.1, r * 0.04);
    ctx.fillStyle = INK;
    ellipse(ctx, p.x, mouthY + r * 0.06, r * 0.13, r * 0.17);
    ctx.fill();
    return;
  }

  if (face === 'cheer') {
    drawGleeEyes(ctx, cam, p.x, eyeY, eyeDX, r);
    drawMouth(ctx, cam, p.x, mouthY, r, 'smile', 1.5);
    return;
  }

  drawEyes(ctx, cam, p.x, eyeY, eyeDX, r, p.skin.eyes, aim);
  drawMouth(ctx, cam, p.x, mouthY, r, p.skin.mouth, 1);
}

function drawEyes(ctx: CanvasRenderingContext2D, cam: Camera, cx: number, y: number, dx: number, r: number, style: EyeStyle, aim: Vec): void {
  switch (style) {
    case 'dot':
      ctx.fillStyle = INK;
      circle(ctx, cx - dx + aim.x * r * 0.06, y + aim.y * r * 0.06, r * 0.11);
      ctx.fill();
      circle(ctx, cx + dx + aim.x * r * 0.06, y + aim.y * r * 0.06, r * 0.11);
      ctx.fill();
      break;
    case 'glee':
      drawWhiteEye(ctx, cam, cx - dx, y, r * 0.2, aim, r * 0.1, r * 0.06);
      drawWhiteEye(ctx, cam, cx + dx, y, r * 0.2, aim, r * 0.1, r * 0.06);
      break;
    case 'sleepy':
      drawWhiteEye(ctx, cam, cx - dx, y, r * 0.2, aim, r * 0.1, r * 0.05);
      drawWhiteEye(ctx, cam, cx + dx, y, r * 0.2, aim, r * 0.1, r * 0.05);
      // Heavy lids: fill the top of each eye with ink.
      ctx.fillStyle = INK;
      for (const ex of [cx - dx, cx + dx]) {
        ctx.beginPath();
        ctx.arc(ex, y, r * 0.2, Math.PI, 0);
        ctx.closePath();
        ctx.fill();
      }
      break;
    case 'square':
      for (const ex of [cx - dx, cx + dx]) {
        const s = r * 0.19;
        ctx.fillStyle = '#fff';
        roundRect(ctx, ex - s, y - s, s * 2, s * 2, s * 0.35);
        ctx.fill();
        inkStroke(ctx, 1.8, cam.scale);
        ctx.stroke();
        ctx.fillStyle = INK;
        roundRect(ctx, ex - s * 0.45 + aim.x * s * 0.35, y - s * 0.45 + aim.y * s * 0.35, s * 0.9, s * 0.9, s * 0.2);
        ctx.fill();
      }
      break;
  }
}

function drawWhiteEye(ctx: CanvasRenderingContext2D, cam: Camera, x: number, y: number, radius: number, aim: Vec, pupil: number, look: number): void {
  ctx.fillStyle = '#fff';
  circle(ctx, x, y, radius);
  ctx.fill();
  inkStroke(ctx, 1.8, cam.scale);
  ctx.stroke();
  ctx.fillStyle = INK;
  circle(ctx, x + aim.x * look, y + aim.y * look, pupil);
  ctx.fill();
  ctx.fillStyle = '#fff';
  circle(ctx, x + aim.x * look - pupil * 0.35, y + aim.y * look - pupil * 0.35, pupil * 0.3);
  ctx.fill();
}

function drawGleeEyes(ctx: CanvasRenderingContext2D, cam: Camera, cx: number, y: number, dx: number, r: number): void {
  inkStroke(ctx, 2.6, cam.scale);
  for (const ex of [cx - dx, cx + dx]) {
    ctx.beginPath();
    ctx.arc(ex, y + r * 0.08, r * 0.18, Math.PI * 1.15, Math.PI * 1.85);
    ctx.stroke();
  }
}

function drawXEyes(ctx: CanvasRenderingContext2D, cam: Camera, x: number, y: number, s: number): void {
  inkStroke(ctx, 2.4, cam.scale);
  ctx.beginPath();
  ctx.moveTo(x - s, y - s);
  ctx.lineTo(x + s, y + s);
  ctx.moveTo(x + s, y - s);
  ctx.lineTo(x - s, y + s);
  ctx.stroke();
}

function drawMouth(ctx: CanvasRenderingContext2D, cam: Camera, x: number, y: number, r: number, style: MouthStyle, big: number): void {
  inkStroke(ctx, 2.2, cam.scale);
  switch (style) {
    case 'smile':
      ctx.beginPath();
      ctx.arc(x, y - r * 0.05, r * 0.28 * big, Math.PI * 0.15, Math.PI * 0.85);
      ctx.stroke();
      break;
    case 'fangs':
      ctx.beginPath();
      ctx.arc(x, y - r * 0.05, r * 0.28, Math.PI * 0.15, Math.PI * 0.85);
      ctx.stroke();
      ctx.fillStyle = '#fff';
      for (const fx of [x - r * 0.15, x + r * 0.15]) {
        ctx.beginPath();
        ctx.moveTo(fx - r * 0.06, y + r * 0.02);
        ctx.lineTo(fx + r * 0.06, y + r * 0.02);
        ctx.lineTo(fx, y + r * 0.18);
        ctx.closePath();
        ctx.fill();
        inkStroke(ctx, 1.4, cam.scale);
        ctx.stroke();
      }
      break;
    case 'flat':
      ctx.beginPath();
      ctx.moveTo(x - r * 0.2, y + r * 0.08);
      ctx.lineTo(x + r * 0.2, y + r * 0.08);
      ctx.stroke();
      break;
    case 'kissy':
      ctx.fillStyle = INK;
      ellipse(ctx, x, y + r * 0.08, r * 0.08, r * 0.1);
      ctx.fill();
      break;
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, rad: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, rad);
}
